import { useState } from "react";
import { X, Camera, Send, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/api";
import { compressImage } from "@/lib/compressImage";
import { toast } from "sonner";

/**
 * End-of-day recap dialog. Operatives send one summary (note + optional photos).
 * Managers receive a notification.
 */
export default function DaySummaryDialog({ open, onClose, onSent }) {
    const [note, setNote] = useState("");
    const [photos, setPhotos] = useState([]); // {dataUrl, name}[]
    const [busy, setBusy] = useState(false);

    if (!open) return null;

    const addFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        for (const f of files) {
            if (f.size > 25 * 1024 * 1024) { toast.error(`Skipped ${f.name}: too big`); continue; }
            try {
                const { dataUrl } = await compressImage(f, { maxDim: 1600, quality: 0.85 });
                setPhotos(prev => [...prev, { dataUrl, name: f.name }]);
            } catch {
                toast.error(`Couldn't add ${f.name}`);
            }
        }
        e.target.value = "";
    };

    const remove = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!note.trim() && photos.length === 0) {
            toast.error("Add a note or at least one photo");
            return;
        }
        setBusy(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const { data } = await api.post("/day-summaries", {
                date: today,
                note: note.trim(),
                photos: photos.map(p => p.dataUrl),
            });
            toast.success("Summary sent to your manager");
            onSent && onSent(data);
            setNote("");
            setPhotos([]);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="day-summary-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="p-6 pb-4 flex items-start justify-between shrink-0 border-b border-[var(--border-soft)]">
                    <div>
                        <h2 className="font-display text-xl font-semibold">Today's summary</h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">One quick recap for your manager — a note and any photos from the day.</p>
                    </div>
                    <button type="button" onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-black" data-testid="close-summary-btn"><X size={18} /></button>
                </div>

                <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">Note</label>
                        <textarea
                            data-testid="summary-note-input"
                            className="input-field mt-1 min-h-[90px] resize-y"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="e.g. Finished all sites. Held up 20 mins at Skerries — alarm needed re-code. All bins out."
                        />
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">Photos (optional)</label>
                        <label className="mt-1 inline-flex items-center gap-2 rounded-full border border-[var(--border-soft)] px-3 py-1.5 text-sm cursor-pointer hover:border-[var(--pine)] transition-colors">
                            <Camera size={14} /> Add photos
                            <input data-testid="summary-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={addFiles} />
                        </label>
                        {photos.length > 0 && (
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                {photos.map((p, i) => (
                                    <div key={i} className="relative group">
                                        <img src={p.dataUrl} alt="" className="w-full h-24 object-cover rounded-lg" />
                                        <button type="button"
                                                data-testid={`remove-photo-${i}`}
                                                onClick={() => remove(i)}
                                                className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-[var(--border-soft)] flex justify-end gap-2 shrink-0">
                    <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                    <button data-testid="send-summary-btn" onClick={submit} disabled={busy}
                            className="btn-primary inline-flex items-center gap-2">
                        <Send size={14} /> {busy ? "Sending…" : "Send to manager"}
                    </button>
                </div>
            </div>
        </div>
    );
}
